import { createI18n } from 'vue-i18n'
import _attempt from 'lodash/attempt'
import _first from 'lodash/first'
import _merge from 'lodash/merge'
import { CookieName, getCookie, setCookie } from '@src/helpers/cookie'
import userApi from '@src/api/user'
import { getPermissionLevel, PermissionLevel } from '@src/utilities/userUtils'
import { isAuthenticated } from '@src/utilities/auth'
import { translationApi } from '@src/api/apiModuleInstances'
import compileIcuMessage from './messageCompiler'
import { DEFAULT_LANGUAGE } from './constants'

const loadedLanguages: string[] = []

const i18n = createI18n({
  legacy: false,
  fallbackLocale: DEFAULT_LANGUAGE,
  fallbackRoot: false,
  fallbackRootWithEmptyString: false,
  silentTranslationWarn: true,
  messageCompiler: compileIcuMessage,
  messages: {}
})

export const getBrowserLanguage = (): string => {
  const browserLang = _attempt(() => {
    const { language } = window.navigator
    return _first(language.split('-'))
  })
  return typeof browserLang === 'string' ? browserLang : DEFAULT_LANGUAGE
}

const setLanguage = (lang: string): string => {
  i18n.global.locale.value = lang
  return lang
}

export const initLanguage = async (
  lang: string = getCookie(CookieName.LANGUAGE) || getBrowserLanguage()
): Promise<string> => {
  try {
    setCookie({
      name: CookieName.LANGUAGE,
      value: lang,
      expiration: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365)
    })
  } catch {
    // ignore
  }

  if (i18n.global.locale.value === lang) {
    return lang
  }

  if (loadedLanguages.includes(lang)) {
    return setLanguage(lang)
  }

  try {
    const { data: translations } = await translationApi.cached.get(lang === 'de' ? 'de-CH' : lang)

    if (await shouldIncludeAdminTranslations()) {
      const { data: adminTranslations } = await translationApi.cached.getAdmin()
      // Deep merge, not a spread: both payloads are nested trees, so a shared top-level
      // group (e.g. "user-profile") in the admin set would otherwise replace the main
      // set's whole subtree and silently drop every non-admin key under it.
      i18n.global.setLocaleMessage(lang, _merge({}, translations, adminTranslations))
    } else {
      i18n.global.setLocaleMessage(lang, translations)
    }
  } catch {
    // Ignore errors, continue booting app
  }

  loadedLanguages.push(lang)
  return setLanguage(lang)
}

async function shouldIncludeAdminTranslations() {
  if (isAuthenticated()) {
    try {
      const { data: roles } = await userApi.cached.getRoles()
      return getPermissionLevel(roles) === PermissionLevel.Admin
    } catch {
      return false
    }
  }
  return false
}

export default i18n
